import { getCurrentReader } from '../auth/getCurrentReader';
const unavailable = "We couldn't load your orders. Please try again.";
const optionalText = value => value == null || typeof value === 'string';
const validOrder = order => order && typeof order.orderId === 'string' && order.orderId.trim() &&
  ['authorName','status','createdAt'].every(key => optionalText(order[key])) &&
  (order.items == null || (Array.isArray(order.items) && order.items.every(item => item && typeof item === 'object' && !Array.isArray(item) &&
    ['title','bookSlug','format'].every(key => optionalText(item[key])) &&
    (item.quantity == null || (Number.isInteger(item.quantity) && item.quantity > 0))))) &&
  (order.shippo == null || (typeof order.shippo === 'object' && !Array.isArray(order.shippo) &&
    ['trackingNumber','trackingUrl'].every(key => optionalText(order.shippo[key]))));

export async function getReaderOrders({ signal } = {}) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener('abort', abort);
  let timer;
  const work = async () => {
    const reader = await getCurrentReader();
    if (!reader?.idToken || !reader.readerSub) throw new Error('Your reader session could not be verified. Please refresh and try again.');
    if (controller.signal.aborted) throw new Error('Request cancelled.');
    const endpoint = process.env.REACT_APP_GET_READER_ORDERS_API_URL;
    if (!endpoint) throw new Error('Order history is not available yet. Please keep your order confirmation email.');
    const response = await fetch(endpoint, {headers:{Authorization:`Bearer ${reader.idToken}`},signal:controller.signal});
    if (response.status === 403) {
      const error = await response.json().catch(() => ({}));
      if (error?.code === 'EMAIL_NOT_VERIFIED') throw new Error('Verify your email address before viewing your orders. Then sign in again and refresh My Orders.');
    }
    if (response.status === 401 || response.status === 403) throw new Error('Your reader session has expired. Please sign in again to view your orders.');
    if (!response.ok) throw new Error(unavailable);
    const data = await response.json();
    const current = await getCurrentReader();
    if (current?.readerSub !== reader.readerSub || !current?.idToken || controller.signal.aborted) throw new Error('Your reader session changed. Refresh to load your orders.');
    if (!Array.isArray(data?.orders) || data.orders.some(order => !validOrder(order))) throw new Error(unavailable);
    const timestamp = value => Date.parse(value) || 0;
    return [...new Map(data.orders.map(order => [order.orderId, order])).values()].sort((a,b) => timestamp(b.createdAt)-timestamp(a.createdAt));
  };
  try {
    return await Promise.race([work(),new Promise((_,reject)=>{timer=setTimeout(()=>{abort();reject(new Error('Loading your orders took too long. Please try again.'));},20000);})]);
  } catch(error) {
    if (error instanceof SyntaxError || error instanceof TypeError) throw new Error(unavailable);
    throw error;
  } finally {clearTimeout(timer);signal?.removeEventListener('abort',abort);}
}
